import type {
  ImageFilterResult,
  ImageFilterProcessResult,
  GrayscaleOptions,
  ThresholdOptions,
  DitheringOptions,
} from './imageFilters.types';

/**
 * Load an image from a data URL to a canvas
 */
async function loadImageToCanvas(imageSrc: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    img.src = imageSrc;
  });
}

/**
 * Convert image data to grayscale using ITU-R BT.709 luminance formula
 */
function convertImageDataToGrayscale(data: Uint8ClampedArray): void {
  for (let i = 0; i < data.length; i += 4) {
    // ITU-R BT.709 luminance formula
    const r = data[i] || 0;
    const g = data[i + 1] || 0;
    const b = data[i + 2] || 0;
    const gray = r * 0.299 + g * 0.587 + b * 0.114;
    data[i] = gray; // R
    data[i + 1] = gray; // G
    data[i + 2] = gray; // B
    // Alpha channel (i + 3) remains unchanged
  }
}

/**
 * Apply brightness and contrast adjustments to image data
 */
function applyBrightnessContrastToImageData(
  data: Uint8ClampedArray,
  brightness: number,
  contrast: number
): void {
  // Map -100..100 to usable ranges
  const brightnessFactor = brightness / 100; // -1 to 1
  const contrastFactor = (contrast + 100) / 100; // 0 to 2

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] || 0;
    const g = data[i + 1] || 0;
    const b = data[i + 2] || 0;

    // Apply contrast first (around midpoint 128)
    let newR = (r - 128) * contrastFactor + 128;
    let newG = (g - 128) * contrastFactor + 128;
    let newB = (b - 128) * contrastFactor + 128;

    // Apply brightness (additive)
    newR += brightnessFactor * 255;
    newG += brightnessFactor * 255;
    newB += brightnessFactor * 255;

    // Clamp to valid range 0-255
    data[i] = Math.max(0, Math.min(255, newR));
    data[i + 1] = Math.max(0, Math.min(255, newG));
    data[i + 2] = Math.max(0, Math.min(255, newB));
  }
}

/**
 * Distribute error to neighboring pixels using Floyd-Steinberg matrix
 */
function distributeError(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
  error: number
): void {
  // Right pixel (x+1, y): 7/16
  if (x + 1 < width) {
    const idx = (y * width + (x + 1)) * 4;
    const current = data[idx] || 0;
    const newValue = Math.max(0, Math.min(255, current + error * (7 / 16)));
    data[idx] = newValue;
    data[idx + 1] = newValue;
    data[idx + 2] = newValue;
  }

  // Bottom-left (x-1, y+1): 3/16
  if (x - 1 >= 0 && y + 1 < height) {
    const idx = ((y + 1) * width + (x - 1)) * 4;
    const current = data[idx] || 0;
    const newValue = Math.max(0, Math.min(255, current + error * (3 / 16)));
    data[idx] = newValue;
    data[idx + 1] = newValue;
    data[idx + 2] = newValue;
  }

  // Bottom (x, y+1): 5/16
  if (y + 1 < height) {
    const idx = ((y + 1) * width + x) * 4;
    const current = data[idx] || 0;
    const newValue = Math.max(0, Math.min(255, current + error * (5 / 16)));
    data[idx] = newValue;
    data[idx + 1] = newValue;
    data[idx + 2] = newValue;
  }

  // Bottom-right (x+1, y+1): 1/16
  if (x + 1 < width && y + 1 < height) {
    const idx = ((y + 1) * width + (x + 1)) * 4;
    const current = data[idx] || 0;
    const newValue = Math.max(0, Math.min(255, current + error * (1 / 16)));
    data[idx] = newValue;
    data[idx + 1] = newValue;
    data[idx + 2] = newValue;
  }
}

/**
 * Apply grayscale conversion with brightness and contrast adjustments
 */
export async function applyGrayscale(
  imageSrc: string,
  options: GrayscaleOptions
): Promise<ImageFilterResult> {
  const canvas = await loadImageToCanvas(imageSrc);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Apply brightness/contrast first
  if (options.brightness !== 0 || options.contrast !== 0) {
    applyBrightnessContrastToImageData(data, options.brightness, options.contrast);
  }

  // Convert to grayscale using luminance formula
  convertImageDataToGrayscale(data);

  ctx.putImageData(imageData, 0, 0);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Apply binary threshold conversion
 */
export async function applyThreshold(
  imageSrc: string,
  options: ThresholdOptions
): Promise<ImageFilterResult> {
  const canvas = await loadImageToCanvas(imageSrc);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Apply brightness/contrast first
  if (options.brightness !== 0 || options.contrast !== 0) {
    applyBrightnessContrastToImageData(data, options.brightness, options.contrast);
  }

  // Convert to grayscale
  convertImageDataToGrayscale(data);

  // Apply binary threshold
  for (let i = 0; i < data.length; i += 4) {
    const pixel = data[i] || 0;
    const value = pixel < options.threshold ? 0 : 255;
    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
  }

  ctx.putImageData(imageData, 0, 0);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Apply Floyd-Steinberg dithering for black and white conversion
 */
export async function applyDithering(
  imageSrc: string,
  options: DitheringOptions
): Promise<ImageFilterResult> {
  const canvas = await loadImageToCanvas(imageSrc);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Apply brightness/contrast first
  if (options.brightness !== 0 || options.contrast !== 0) {
    applyBrightnessContrastToImageData(data, options.brightness, options.contrast);
  }

  // Convert to grayscale
  convertImageDataToGrayscale(data);

  // Floyd-Steinberg error diffusion
  const intensityFactor = options.intensity / 100;

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const idx = (y * canvas.width + x) * 4;
      const oldPixel = data[idx] || 0; // R channel (grayscale, so R=G=B)

      // Quantize to black or white
      const newPixel = oldPixel < 128 ? 0 : 255;
      data[idx] = newPixel;
      data[idx + 1] = newPixel;
      data[idx + 2] = newPixel;

      // Calculate error
      const error = (oldPixel - newPixel) * intensityFactor;

      // Distribute error to neighbors using Floyd-Steinberg matrix
      distributeError(data, canvas.width, canvas.height, x, y, error);
    }
  }

  ctx.putImageData(imageData, 0, 0);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Clone ImageData immutably (returns a new copy)
 */
function cloneImageData(source: ImageData): ImageData {
  return new ImageData(new Uint8ClampedArray(source.data), source.width, source.height);
}

/**
 * Process grayscale conversion on raw ImageData (no decode/encode overhead).
 * Returns a new ImageData — input is not mutated.
 */
export function processGrayscale(
  source: ImageData,
  options: GrayscaleOptions
): ImageFilterProcessResult {
  const result = cloneImageData(source);
  const data = result.data;

  if (options.brightness !== 0 || options.contrast !== 0) {
    applyBrightnessContrastToImageData(data, options.brightness, options.contrast);
  }
  convertImageDataToGrayscale(data);

  return { imageData: result, width: result.width, height: result.height };
}

/**
 * Process threshold conversion on raw ImageData (no decode/encode overhead).
 * Returns a new ImageData — input is not mutated.
 */
export function processThreshold(
  source: ImageData,
  options: ThresholdOptions
): ImageFilterProcessResult {
  const result = cloneImageData(source);
  const data = result.data;

  if (options.brightness !== 0 || options.contrast !== 0) {
    applyBrightnessContrastToImageData(data, options.brightness, options.contrast);
  }
  convertImageDataToGrayscale(data);

  for (let i = 0; i < data.length; i += 4) {
    const pixel = data[i] || 0;
    const value = pixel < options.threshold ? 0 : 255;
    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
  }

  return { imageData: result, width: result.width, height: result.height };
}

/**
 * Process Floyd-Steinberg dithering on raw ImageData (no decode/encode overhead).
 * Returns a new ImageData — input is not mutated.
 */
export function processDithering(
  source: ImageData,
  options: DitheringOptions
): ImageFilterProcessResult {
  const result = cloneImageData(source);
  const data = result.data;
  const { width, height } = result;

  if (options.brightness !== 0 || options.contrast !== 0) {
    applyBrightnessContrastToImageData(data, options.brightness, options.contrast);
  }
  convertImageDataToGrayscale(data);

  const intensityFactor = options.intensity / 100;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const oldPixel = data[idx] || 0;
      const newPixel = oldPixel < 128 ? 0 : 255;
      data[idx] = newPixel;
      data[idx + 1] = newPixel;
      data[idx + 2] = newPixel;

      const error = (oldPixel - newPixel) * intensityFactor;
      distributeError(data, width, height, x, y, error);
    }
  }

  return { imageData: result, width, height };
}
